import {
  TracesModel,
  IOtelSpanFilter,
  OtelSpanDocument,
  OtelSpanDocumentWithFieldTypes,
  SpanAggregationsResponse,
  ISpanCompareReportRequest,
  ISpanCompareReportResponse,
  ISpanCompareDistributionRequest,
  ISpanCompareDistributionResponse,
  SpanGraphMetric,
  SpanLatencyPercentile,
  SpanGraphResponse,
} from '@logz-pkg/models';
import { ICollectionResponse, ISearchRequestObject, ISort } from '@logz-build/typescript';
import { CrudApiService } from '@logz-pkg/frontend-services/src/dal/utilities/crud.api.service';

class TracingExploreApiService {
  crudApiService = new CrudApiService<TracesModel & OtelSpanDocument & OtelSpanDocumentWithFieldTypes>();

  urls = {
    searchTraces: '/observability-apps/tracing/v1/services/traces/search',
    searchSpans: '/observability-apps/tracing/v1/spans/search',
    spanAggregations: '/observability-apps/tracing/v1/spans/aggregations',
    spanCompareReport: '/observability-apps/tracing/v1/spans/compare/report',
    spanCompareDistribution: '/observability-apps/tracing/v1/spans/compare/distribution',
    spanGraph: '/observability-apps/tracing/v1/spans/graph',
    searchTrace: '/observability-apps/tracing/v1/traces/search',
  };

  fetchServicesSpans = async (values: ISearchRequestObject): Promise<ICollectionResponse<TracesModel>> =>
    this.crudApiService.do(this.urls.searchTraces, {
      payload: values,
    }) ?? { results: [], total: 0 };

  fetchSpans = async (payload: {
    filter: {
      start: string | number;
      end: string | number;
      accounts?: string[];
      spanFilters?: IOtelSpanFilter[];
      luceneQuery?: string;
    };
    pagination?: { pageNumber: number; pageSize: number };
    sort?: ISort[];
  }): Promise<ICollectionResponse<OtelSpanDocument>> =>
    this.crudApiService.do(this.urls.searchSpans, { payload }) ?? { results: [], total: 0 };

  fetchSpanAggregations = async (payload: {
    filter: {
      start: string | number;
      end: string | number;
      accounts?: string[];
      spanFilters?: IOtelSpanFilter[];
      luceneQuery?: string;
    };
    limit?: number;
  }): Promise<SpanAggregationsResponse> =>
    this.crudApiService.do(this.urls.spanAggregations, { payload }) ?? {
      totalSpanCount: 0,
      aggregations: {
        'service.name': { field: 'service.name', total: 0, results: [] },
        'otel.status_code': {
          field: 'otel.status_code',
          total: 3,
          results: [
            { value: 'OK', count: 0 },
            { value: 'ERROR', count: 0 },
            { value: 'UNSET', count: 0 },
          ],
        },
        'span.name': { field: 'span.name', total: 0, results: [] },
      },
    };

  // Span Compare structured endpoints — the frontend sends OTEL field names only; all Jaeger
  // translation and OpenSearch DSL building happen in observability-apps. Responses are returned
  // as-is (no fabricated empty fallback) so callers can tell a degraded/blank response apart from
  // a legitimately empty result and surface it loudly.
  getSpanCompareReport = async (payload: ISpanCompareReportRequest): Promise<ISpanCompareReportResponse> =>
    this.crudApiService.do<ISpanCompareReportResponse>(this.urls.spanCompareReport, { payload });

  getSpanFieldDistribution = async (
    payload: ISpanCompareDistributionRequest,
  ): Promise<ISpanCompareDistributionResponse> =>
    this.crudApiService.do<ISpanCompareDistributionResponse>(this.urls.spanCompareDistribution, { payload });

  fetchSpanGraph = async (payload: {
    filter: {
      start: string | number;
      end: string | number;
      accounts?: string[];
      spanFilters?: IOtelSpanFilter[];
      luceneQuery?: string;
    };
    metric: SpanGraphMetric;
    latencyPercentile?: SpanLatencyPercentile;
    groupBy: string;
    timeInterval?: string;
  }): Promise<SpanGraphResponse> =>
    this.crudApiService.do(this.urls.spanGraph, { payload }) ?? {
      groupBySeries: [],
      resolvedGroupField: '',
    };

  fetchTrace = async (payload: {
    filter: {
      start: string | number;
      end: string | number;
      traceId: string;
      tracingAccountId?: number;
    };
  }): Promise<ICollectionResponse<OtelSpanDocumentWithFieldTypes>> =>
    this.crudApiService.do(this.urls.searchTrace, { payload }) ?? { results: [], total: 0 };
}

export const tracingExploreApiService = new TracingExploreApiService();
